/**
 * Profile hook — mutations for profile updates + avatar management.
 * Reuses the profile query from useAuth() to avoid cache duplication.
 */
import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { profileService } from '../services/profile.service'
import { useAuth, authKeys } from './use-auth'
import { calculateProfileCompletion } from '../utils/profile-completion'
import type { UpdateProfileInput } from '../validations/profile.schema'
import type { ProfileCompletion, ProfileCompletionStep } from '../utils/profile-completion'

export function useProfile() {
  const queryClient = useQueryClient()
  const { user, profile } = useAuth()

  const invalidateProfile = () => {
    if (user) {
      queryClient.invalidateQueries({ queryKey: authKeys.profile(user.id) })
    }
  }

  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileInput) => profileService.updateProfile(user!.id, data),
    // Optimistic patch — profile fields appear updated instantly, server
    // round-trips in the background. Validation already ran client-side
    // (UpdateProfileInput is the same Zod schema the service enforces),
    // so failure modes are network/auth, not bad data. We snapshot the
    // prior profile and roll back on error so the UI stays truthful.
    onMutate: async (data) => {
      if (!user) return undefined
      const key = authKeys.profile(user.id)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)
      queryClient.setQueryData(key, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        return { ...old, ...data }
      })
      return { previous, key }
    },
    onError: (_err, _data, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: invalidateProfile,
  })

  const uploadAvatarMutation = useMutation({
    // No user id passed on purpose: the service derives it from the LIVE session
    // so the storage path always matches auth.uid() (a cached id can drift and
    // fail storage RLS). See profileService.uploadAvatar.
    mutationFn: (imageUri: string) => profileService.uploadAvatar(imageUri),
    onSuccess: invalidateProfile,
  })

  const deleteAvatarMutation = useMutation({
    mutationFn: () => profileService.deleteAvatar(),
    onSuccess: invalidateProfile,
  })

  // Profile completion — weighted percentage reflecting real business value.
  // Each step has a weight proportional to its impact on trust & bookings.
  // Returns BOTH the headline percentage (for backwards compat) and a
  // structured `missingFields` list so the UI can render per-field nudges
  // ("Agregá tu teléfono →") instead of a passive progress bar.
  const completion = useMemo<ProfileCompletion>(() => {
    if (!profile) return { percent: 0, missingFields: [] }

    return calculateProfileCompletion(profile)
  }, [profile])

  return {
    profile,
    /** @deprecated Use `completion.percent`. Kept for back-compat with screens
     * that still read the headline number directly. */
    completionPercentage: completion.percent,
    completion,

    updateProfile: updateProfileMutation.mutateAsync,
    uploadAvatar: uploadAvatarMutation.mutateAsync,
    deleteAvatar: deleteAvatarMutation.mutateAsync,

    updatePending: updateProfileMutation.isPending,
    uploadAvatarPending: uploadAvatarMutation.isPending,
    deleteAvatarPending: deleteAvatarMutation.isPending,
  }
}

// ─── Public types ──────────────────────────────────────────────────────────

export type { ProfileCompletion, ProfileCompletionStep }
